import React, { useEffect } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Edges, Html } from "@react-three/drei";
import "./PalletVisualizer.css";

function reorderContainerDims(orientation, w, h, d) {
    switch (orientation) {
        case "whd": return [w, h, d];
        case "wdh": return [w, d, h];
        case "hwd": return [h, w, d];
        case "hdw": return [h, d, w];
        case "dwh": return [d, w, h];
        case "dhw": return [d, h, w];
        default: return [w, h, d];
    }
}

function permuteDims(rotationType, w, h, d) {
    switch (rotationType) {
        case 1: return [h, w, d];
        case 2: return [h, d, w];
        case 3: return [d, h, w];
        case 4: return [d, w, h];
        case 5: return [w, d, h];
        case 0:
        default: return [w, h, d];
    }
}

function fixCornerForOrientation(rotationType, corner, w, h, d) {
    const c = [...corner];
    switch (rotationType) {
        case 0: c[0] += w / 2; c[1] += h / 2; c[2] += d / 2; break;
        case 1: c[0] += h / 2; c[1] += w / 2; c[2] += d / 2; break;
        case 2: c[0] += h / 2; c[1] += d / 2; c[2] += w / 2; break;
        case 3: c[0] += d / 2; c[1] += h / 2; c[2] += w / 2; break;
        case 4: c[0] += d / 2; c[1] += w / 2; c[2] += h / 2; break;
        case 5: c[0] += w / 2; c[1] += d / 2; c[2] += h / 2; break;
        default: break;
    }
    return c;
}

function axisIndex(c) {
    return c === "w" ? 0 : c === "h" ? 1 : 2;
}

function buildSolverToWorld(orientation) {
    const o = orientation || "whd";
    return [axisIndex(o[0]), axisIndex(o[1]), axisIndex(o[2])];
}

function mapToWorld(arr, mapping) {
    const out = [0, 0, 0];
    out[mapping[0]] = arr[0];
    out[mapping[1]] = arr[1];
    out[mapping[2]] = arr[2];
    return out;
}

const DEFAULT_BOX_COLOR = "#86EFAC";

const AXIS_COLORS = { w: "#E8890C", h: "#2563EB", d: "#16A34A" };

function fmtDim(v) {
    const n = Number(v);
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// Camera-facing dimension chip pinned to a container edge midpoint, so
// W/H/D stay identifiable from any orbit angle.
function DimLabel({ position, axis, value }) {
    return (
        <Html position={position} center zIndexRange={[4, 0]}>
            <div
                className="viz-dim-label"
                style={{ borderColor: AXIS_COLORS[axis], color: AXIS_COLORS[axis] }}
            >
                {axis.toUpperCase()} {fmtDim(value)}″
            </div>
        </Html>
    );
}

// Recompute camera position from the actual canvas aspect so the scene
// fits in both portrait and landscape, and refit on resize / orientation
// change. Saves controls state so OrbitControls.reset() returns here.
function CameraFitter({ targetSize }) {
    const { camera, size, controls } = useThree();
    useEffect(() => {
        if (!size.width || !size.height || !targetSize) return;
        const aspect = size.width / size.height;
        const fov = ((camera.fov ?? 35) * Math.PI) / 180;
        const halfV = fov / 2;
        const halfH = Math.atan(aspect * Math.tan(halfV));
        const minHalf = Math.min(halfV, halfH);
        // Conservative bounding sphere radius for an axis-aligned cube
        // of side `targetSize`: half the body diagonal.
        const radius = (targetSize * Math.sqrt(3)) / 2;
        const distance = (radius / Math.sin(minHalf)) * 1.15; // 15% margin
        camera.position.set(1, 1, 1).normalize().multiplyScalar(distance);
        camera.lookAt(0, 0, 0);
        camera.updateProjectionMatrix();
        if (controls?.target) {
            controls.target.set(0, 0, 0);
            controls.update?.();
            controls.saveState?.();
        }
    }, [targetSize, size.width, size.height, camera, controls]);
    return null;
}

function PalletVisualizer({
    container,
    placements = [],
    bestOrientation,
    displayCount = 0,
    controlsRef,
    containerEdgeColor = "#E8890C",
    activeBoxColor = "#EF4444",
}) {
    const W = Number(container.width);
    const H = Number(container.height);
    const D = Number(container.depth);

    const orientation = bestOrientation || "whd";
    const mapping = buildSolverToWorld(orientation);
    const [cW, cH, cD] = reorderContainerDims(orientation, W, H, D);

    const toWorld = (placement) => {
        const rawX = Number(placement.position.x);
        const rawY = Number(placement.position.y);
        const rawZ = Number(placement.position.z);

        const oW = Number(placement.dimensions.width);
        const oH = Number(placement.dimensions.height);
        const oD = Number(placement.dimensions.depth);

        const [solverDimX, solverDimY, solverDimZ] = permuteDims(
            placement.rotation_type,
            oW, oH, oD
        );
        const solverCenter = fixCornerForOrientation(
            placement.rotation_type,
            [rawX, rawY, rawZ],
            oW, oH, oD
        );

        const sx = solverCenter[0] - cW / 2;
        const sy = solverCenter[1] - cH / 2;
        const sz = solverCenter[2] - cD / 2;

        const worldPos = mapToWorld([sx, sy, sz], mapping);
        const worldDims = mapToWorld([solverDimX, solverDimY, solverDimZ], mapping);

        return {
            dims: worldDims,
            corner: worldPos,
            color: placement.color || DEFAULT_BOX_COLOR,
        };
    };

    const allProps = placements.map(toWorld);
    const yOffset = allProps.length
        ? -H / 2 - Math.min(...allProps.map((p) => p.corner[1] - p.dims[1] / 2))
        : 0;

    const visibleProps = allProps.slice(0, displayCount);
    const targetSize = Math.max(W, H, D);
    const initialDist = targetSize * 2.5;

    return (
        <div className="visualizer-canvas-wrapper">
            <Canvas camera={{ position: [initialDist, initialDist, initialDist], fov: 35 }}>
                <ambientLight intensity={0.55} />
                <pointLight position={[40, 40, 40]} intensity={0.8} />
                <pointLight position={[-30, 20, -30]} intensity={0.3} />
                <OrbitControls
                    ref={controlsRef}
                    makeDefault
                    enableDamping
                    dampingFactor={0.08}
                />
                <CameraFitter targetSize={targetSize} />

                <mesh position={[0, 0, 0]}>
                    <boxGeometry args={[W, H, D]} />
                    <meshStandardMaterial
                        color="#23262F"
                        opacity={0.05}
                        transparent
                        depthWrite={false}
                    />
                    <Edges scale={1.001} color={containerEdgeColor} />
                </mesh>

                <DimLabel position={[0, -H / 2, D / 2]} axis="w" value={W} />
                <DimLabel position={[-W / 2, 0, D / 2]} axis="h" value={H} />
                <DimLabel position={[-W / 2, -H / 2, 0]} axis="d" value={D} />

                {visibleProps.map(({ dims, corner, color }, idx) => {
                    const isLast = idx === visibleProps.length - 1;
                    const pos = [corner[0], corner[1] + yOffset, corner[2]];
                    return (
                        <mesh key={idx} position={pos}>
                            <boxGeometry args={dims} />
                            <meshStandardMaterial color={isLast ? activeBoxColor : color} />
                            <Edges scale={1.001} color="#0E1014" />
                        </mesh>
                    );
                })}
            </Canvas>
        </div>
    );
}

export default PalletVisualizer;
